/* global globalThis */
import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  useNavigate,
  useParams,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import axiosClient from "../../../api-client/AxiosClient";
import { priorityOptions } from "./taskUtils";
import WorkItemIcon from "./Workitemicon";

const WORK_ITEM_TYPES = [
  {
    value: "bug",
    label: "Bug",
    color: "#EF4444",
    bg: "#FEE2E2",
    border: "#FECACA",
  },
  {
    value: "user-story",
    label: "User Story",
    color: "#3B82F6",
    bg: "#EFF6FF",
    border: "#BFDBFE",
  },
  {
    value: "feature",
    label: "Feature",
    color: "#F59E0B",
    bg: "#FFFBEB",
    border: "#FDE68A",
  },
  {
    value: "task",
    label: "Task",
    color: "#10B981",
    bg: "#ECFDF5",
    border: "#A7F3D0",
  },
  {
    value: "epic",
    label: "Epic",
    color: "#8B5CF6",
    bg: "#F5F3FF",
    border: "#DDD6FE",
  },
];

const TABS = ["Details", "Comments", "Sub Tasks", "Attachments", "Activity"];

const STATUS_CONFIG = {
  1: { label: "New", color: "#6B7280", bg: "#F3F4F6" },
  2: { label: "In Progress", color: "#2563EB", bg: "#EFF6FF" },
  3: { label: "Closed", color: "#059669", bg: "#ECFDF5" },
};
const TaskCreatedIcon = ({ color }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="16" />
    <line x1="8" y1="12" x2="16" y2="12" />
  </svg>
);

TaskCreatedIcon.propTypes = {
  color: PropTypes.string.isRequired,
};
const TaskUpdatedIcon = ({ color }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

TaskUpdatedIcon.propTypes = {
  color: PropTypes.string.isRequired,
};
const StatusChangedIcon = ({ color }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="17 1 21 5 17 9" />
    <path d="M3 11V9a4 4 0 0 1 4-4h14" />
    <polyline points="7 23 3 19 7 15" />
    <path d="M21 13v2a4 4 0 0 1-4 4H3" />
  </svg>
);

StatusChangedIcon.propTypes = {
  color: PropTypes.string.isRequired,
};
const PriorityChangedIcon = ({ color }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
    <line x1="4" y1="22" x2="4" y2="15" />
  </svg>
);

PriorityChangedIcon.propTypes = {
  color: PropTypes.string.isRequired,
};
const CommentAddedIcon = ({ color }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

CommentAddedIcon.propTypes = {
  color: PropTypes.string.isRequired,
};
const AttachmentAddedIcon = ({ color }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
);

AttachmentAddedIcon.propTypes = {
  color: PropTypes.string.isRequired,
};
const AttachmentDeletedIcon = ({ color }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
    <path d="M9 6V4h6v2" />
  </svg>
);

AttachmentDeletedIcon.propTypes = {
  color: PropTypes.string.isRequired,
};
const DefaultActivityIcon = ({ color }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="1" fill={color} />
  </svg>
);

DefaultActivityIcon.propTypes = {
  color: PropTypes.string.isRequired,
};

const ACTIVITY_TYPE_CONFIG = {
  TaskCreated: { color: "#10B981", bg: "#ECFDF5", SvgIcon: TaskCreatedIcon },
  TaskUpdated: { color: "#2563EB", bg: "#EFF6FF", SvgIcon: TaskUpdatedIcon },
  StatusChanged: {
    color: "#8B5CF6",
    bg: "#F5F3FF",
    SvgIcon: StatusChangedIcon,
  },
  PriorityChanged: {
    color: "#F59E0B",
    bg: "#FFFBEB",
    SvgIcon: PriorityChangedIcon,
  },
  CommentAdded: { color: "#06B6D4", bg: "#ECFEFF", SvgIcon: CommentAddedIcon },
  AttachmentAdded: {
    color: "#EA580C",
    bg: "#FFF7ED",
    SvgIcon: AttachmentAddedIcon,
  },
  AttachmentDeleted: {
    color: "#EF4444",
    bg: "#FEF2F2",
    SvgIcon: AttachmentDeletedIcon,
  },
};

// NOSONAR
const CreateWorkItem = () => {
  const navigate = useNavigate();
  const { taskId } = useParams();
  const [searchParams] = useSearchParams();
  const isEditMode = Boolean(taskId);

  const location = useLocation();
  const fromView = location.state?.fromView || "dashboard";

  const [selectedType, setSelectedType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchingTask, setFetchingTask] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(0);
  const [activeTab, setActiveTab] = useState("Details");
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  const [childTasks, setChildTasks] = useState([]);
  const [loadingChildTasks, setLoadingChildTasks] = useState(false);
  const [originalTask, setOriginalTask] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState(null);
  const [activitySummary, setActivitySummary] = useState(null);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [showSubTaskForm, setShowSubTaskForm] = useState(false);
  const [modules, setModules] = useState([]);
  const [loadingModules, setLoadingModules] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);

  const [moduleSearchInput, setModuleSearchInput] = useState("");
  const [creatingModule, setCreatingModule] = useState(false);
  const [deletingAttachmentConfirmId, setDeletingAttachmentConfirmId] =
    useState(null);
  const [subTaskFormData, setSubTaskFormData] = useState({
    subject: "",
    description: "",
    assignedTo: null,
    priority: null,
    dueDate: "",
    taskType: null,
  });
  const [creatingSubTask, setCreatingSubTask] = useState(false);

  const [formData, setFormData] = useState({
    subject: "",
    description: "",
    assignedTo: null,
    assignedBy: null,
    priority: null,
    dueDate: "",
    startDate: "",
    endDate: "",
    efforts: "",
    taskType: null,
    status: 1,
    isRead: false,
    isDeleted: false,
    createdBy: "0",
    parentId: "0",
    moduleId: null,
  });

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("userData"));
    const userId = userData?.id || 0;
    setCurrentUserId(userId);
    fetchUsers();
    fetchTaskTypes();
    fetchModules(); // ← ADD THIS
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isEditMode) {
      const typeParam = searchParams.get("type");
      if (typeParam) {
        const found = WORK_ITEM_TYPES.find((t) => t.value === typeParam);
        if (found) {
          setSelectedType(found);
          // Pre-fill taskType in formData so it gets sent in the payload
        }
      }
    }
  }, [searchParams, isEditMode]);

  const isFirstLoad = useRef(true);

  useEffect(() => {
    if (!isEditMode) return;
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      return;
    }
    setIsFormDirty(true);
  }, [formData, isEditMode]);

  // Add this useEffect after existing ones in CreateWorkItem:
  useEffect(() => {
    if (!isEditMode && selectedType && taskTypes.length > 0) {
      const matched = taskTypes.find(
        (t) => t.name?.toLowerCase() === selectedType.label?.toLowerCase(),
      );
      if (matched) {
        setFormData((prev) => ({
          ...prev,
          taskType: { value: matched.task_type_id, label: matched.name },
        }));
      }
    }
  }, [selectedType, taskTypes, isEditMode]);

  useEffect(() => {
    if (isEditMode && taskId && usersList.length > 0 && taskTypes.length > 0) {
      fetchTaskForEdit();
    }
  }, [isEditMode, taskId, usersList, taskTypes]);

  const fetchUsers = async () => {
    try {
      const response = await axiosClient.get("/api/User/all");
      if (response?.data) {
        const users = Array.isArray(response.data) ? response.data : [];
        const activeUsers = users.filter((user) => user.isactive === true);
        setUsersList(activeUsers);
      } else {
        setUsersList([]);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsersList([]);
    }
  };

  const parseActivityValue = (valueStr) => {
    if (!valueStr) return {};
    const result = {};
    // Split by " | " and parse each "Key: Value" pair
    valueStr.split(" | ").forEach((part) => {
      const colonIdx = part.indexOf(": ");
      if (colonIdx !== -1) {
        const key = part.substring(0, colonIdx).trim();
        const val = part.substring(colonIdx + 2).trim();
        result[key] = val;
      }
    });
    return result;
  };

  const formatActivityValue = (parsed) => {
    return Object.entries(parsed)
      .map(([k, v]) => `${k}: ${v}`)
      .join(" | ");
  };

  const resolveActivityValue = (valueStr) => {
    if (!valueStr) return "";
    const parsed = parseActivityValue(valueStr);

    // Replace AssignedTo ID with name
    if (parsed["AssignedTo"]) {
      const user = usersList.find((u) => u.id === parsed["AssignedTo"]);
      if (user) {
        parsed["AssignedTo"] = `${user.first_name} ${user.last_name}`;
      }
    }

    return parsed;
  };

  const getChangedFields = (oldStr, newStr) => {
    const oldParsed = resolveActivityValue(oldStr);
    const newParsed = resolveActivityValue(newStr);

    const changedOld = {};
    const changedNew = {};

    const allKeys = new Set([
      ...Object.keys(oldParsed),
      ...Object.keys(newParsed),
    ]);

    allKeys.forEach((key) => {
      if (oldParsed[key] !== newParsed[key]) {
        if (oldParsed[key] !== undefined) changedOld[key] = oldParsed[key];
        if (newParsed[key] !== undefined) changedNew[key] = newParsed[key];
      }
    });

    return {
      oldDisplay: formatActivityValue(changedOld),
      newDisplay: formatActivityValue(changedNew),
    };
  };

  const fetchModules = async () => {
    setLoadingModules(true);
    try {
      const res = await axiosClient.get("/api/Task/modules");
      setModules(Array.isArray(res?.data) ? res.data : []);
    } catch (error) {
      console.error("Failed to fetch modules:", error?.message || error);
      setModules([]);
    } finally {
      setLoadingModules(false);
    }
  };

  const handleCreateSubTask = async () => {
    if (
      !subTaskFormData.subject.trim() ||
      !subTaskFormData.assignedTo ||
      !subTaskFormData.priority
    ) {
      toast.error("Title, assignee and priority are required");
      return;
    }
    setCreatingSubTask(true);
    try {
      const payload = {
        id: "0",
        parentid: taskId, // ← current task is the parent
        typeId:
          subTaskFormData.taskType?.value || formData.taskType?.value || "",
        assignedBy: String(currentUserId || "0"),
        assignedTo: subTaskFormData.assignedTo.value,
        subject: subTaskFormData.subject.trim(),
        description: subTaskFormData.description || "",
        priority: subTaskFormData.priority.value,
        isRead: false,
        status: 1,
        dueDate: buildDueDateString(subTaskFormData.dueDate),
        isDeleted: false,
      };
      await axiosClient.post("/api/Task", payload);
      toast.success("Sub task created successfully!");
      setShowSubTaskForm(false);
      setSubTaskFormData({
        subject: "",
        description: "",
        assignedTo: null,
        priority: null,
        dueDate: "",
        taskType: null,
      });
      fetchChildTasks(taskId);
    } catch (e) {
      console.error(e);
      toast.error("Failed to create sub task");
    } finally {
      setCreatingSubTask(false);
    }
  };

  const fetchTaskTypes = async () => {
    try {
      const res = await axiosClient.get("/api/Task/TaskType");
      if (res?.data) {
        setTaskTypes(Array.isArray(res.data) ? res.data : []);
      } else {
        setTaskTypes([]);
      }
    } catch (error) {
      console.error("Failed to fetch task types:", error?.message || error);
      setTaskTypes([]);
    }
  };

  const fetchActivitySummary = async (id) => {
    setLoadingActivity(true);
    try {
      const res = await axiosClient.get(`/api/Task/${id}/activities/summary`);
      setActivitySummary(res?.data || null);
    } catch (error) {
      console.error(
        "Failed to fetch activity summary:",
        error?.message || error,
      );
      setActivitySummary(null);
    } finally {
      setLoadingActivity(false);
    }
  };

  const formatUserOption = (user) => {
    if (!user) return null;
    return {
      value: user.id,
      label: `${user.first_name} ${user.last_name}`,
    };
  };

  const getPriorityOption = (priority) => {
    const priorityNum =
      typeof priority === "string"
        ? ({ High: 1, Medium: 2, Low: 3 }[priority] ?? 2)
        : priority;
    return priorityOptions.find((p) => p.value === priorityNum);
  };

  const getStatusNumber = (status) => {
    if (typeof status === "string") {
      return { New: 1, "In Progress": 2, Closed: 3 }[status] ?? 1;
    }
    return status;
  };

  const findWorkItemTypeMatch = (typeOptName, taskType) => {
    return WORK_ITEM_TYPES.find(
      (w) =>
        w.label.toLowerCase() === typeOptName?.toLowerCase() ||
        w.label.toLowerCase() === taskType?.toLowerCase(),
    );
  };

  const resolveWorkItemType = (typeOpt, task) => {
    const defaultType = WORK_ITEM_TYPES[3];
    if (!typeOpt && !task.type) return defaultType;
    if (typeOpt) {
      const found = findWorkItemTypeMatch(typeOpt.name, task.type);
      return found || defaultType;
    }
    const found = WORK_ITEM_TYPES.find(
      (w) => w.label.toLowerCase() === task.type.toLowerCase(),
    );
    return found || defaultType;
  };

  const applyModuleIfPresent = (task) => {
    if (!task.moduleId) return;
    const matchedModule = modules.find((m) => m.moduleId === task.moduleId);
    if (!matchedModule) return;
    setFormData((prev) => ({
      ...prev,
      moduleId: {
        value: matchedModule.moduleId,
        label: matchedModule.moduleName,
      },
    }));
  };

  const isValidDate = (dateStr) => {
    return dateStr && !dateStr.startsWith("0001") && !dateStr.startsWith("1970");
  };

  const parseDate = (dateStr) => {
    return isValidDate(dateStr) ? new Date(dateStr).toISOString().split("T")[0] : "";
  };

  const buildFormDataFromTask = (task, assignedToUser, assignedByUser, priorityOpt, typeOpt, statusNum) => {
    return {
      subject: task.subject || "",
      description: task.description || "",
      assignedTo: formatUserOption(assignedToUser),
      assignedBy: formatUserOption(assignedByUser),
      priority: priorityOpt || null,
      dueDate: task.dueDate ? new Date(task.dueDate).toISOString().split("T")[0] : "",
      startDate: parseDate(task.startDate),
      endDate: parseDate(task.endDate),
      efforts: task.efforts ?? "",
      taskType: typeOpt ? { value: typeOpt.task_type_id, label: typeOpt.name } : null,
      status: statusNum,
      isRead: task.isRead || false,
      isDeleted: task.isDeleted || false,
      createdBy: String(task.createdBy || "0"),
      parentId: String(task.parentid ?? 0),
    };
  };

  const fetchTaskForEdit = async () => {
    isFirstLoad.current = true;
    setIsFormDirty(false);
    setFetchingTask(true);
    try {
      const res = await axiosClient.get(`/api/Task/${taskId}`);
      const task = res?.data;
      if (!task) {
        console.warn("No task data received from API");
        return;
      }
      setOriginalTask(task);

      const assignedToUser = usersList.find(
        (userData) => userData.id === (task.assignedToId || task.assignedTo),
      );
      const assignedByUser = usersList.find(
        (userData) => userData.id === (task.assignedById || task.assignedBy),
      );
      const priorityOpt = getPriorityOption(task.priority);
      const typeOpt = taskTypes.find(
        (t) =>
          String(t.task_type_id) === String(task.taskTypeId) ||
          String(t.task_type_id) === String(task.typeId),
      );
      const statusNum = getStatusNumber(task.status);

      setFormData(buildFormDataFromTask(task, assignedToUser, assignedByUser, priorityOpt, typeOpt, statusNum));
      applyModuleIfPresent(task);
      setSelectedType(resolveWorkItemType(typeOpt, task));

      fetchComments(taskId);
      fetchChildTasks(taskId);
      fetchAttachments(taskId);
      fetchActivitySummary(taskId);
    } catch (error) {
      console.error("Failed to fetch task:", error?.message || error);
      toast.error("Failed to load task details");
    } finally {
      setFetchingTask(false);
    }
  };

  useEffect(() => {
    if (isEditMode && formData.taskType) {
      const found = WORK_ITEM_TYPES.find(
        (w) => w.label.toLowerCase() === formData.taskType.label?.toLowerCase(),
      );
      if (found) setSelectedType(found);
    }
  }, [formData.taskType, isEditMode]);

  const fetchComments = async (id) => {
    setLoadingComments(true);
    try {
      const res = await axiosClient.get(`/api/Task/allcomments/${id}`);
      const list = Array.isArray(res?.data) ? res.data : [];
      const mapped = list.map((comment) => {
        const user = usersList.find(
          (userData) => userData.id === comment.userId,
        );
        return {
          ...comment,
          userName: user
            ? `${user.first_name} ${user.last_name}`
            : "Unknown User",
        };
      });
      const reversedComments = [...mapped].reverse();
      setComments(reversedComments);
    } catch (error) {
      console.error("Failed to fetch comments:", error?.message || error);
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  };

  const resolveUserName = (userId) => {
    const user = usersList.find((userData) => userData.id === userId);
    if (user) {
      return `${user.first_name} ${user.last_name}`;
    }
    return typeof userId === "string" ? `${userId.slice(0, 8)}…` : "Unknown";
  };

  const fetchChildTasks = async (id) => {
    setLoadingChildTasks(true);
    try {
      const res = await axiosClient.get(`/api/Task/allchilditems/${id}`);
      setChildTasks(Array.isArray(res?.data) ? res.data : []);
    } catch (error) {
      console.error("Failed to fetch child tasks:", error?.message || error);
      setChildTasks([]);
    } finally {
      setLoadingChildTasks(false);
    }
  };

  // AFTER
  const fetchAttachments = async (id) => {
    setLoadingAttachments(true);
    try {
      const res = await axiosClient.get(`/api/Task/${id}/attachments`);
      const data = res?.data;
      // API returns { attachments: [...], attachmentCount, ... }
      setAttachments(Array.isArray(data?.attachments) ? data.attachments : []);
    } catch (error) {
      console.error("Failed to fetch attachments:", error?.message || error);
      setAttachments([]);
    } finally {
      setLoadingAttachments(false);
    }
  };

  const handleUploadAttachment = async (uploadEvent) => {
    const files = Array.from(uploadEvent.target.files);
    if (!files.length) {
      return;
    }

    setUploadingAttachment(true);
    try {
      const formPayload = new FormData();
      files.forEach((file) => formPayload.append("attachments", file));

      await axiosClient.post(`/api/Task/${taskId}/attachments`, formPayload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const fileCountText =
        files.length > 1 ? `${files.length} files` : files[0].name;
      toast.success(`${fileCountText} uploaded successfully!`);
      fetchAttachments(taskId);
    } catch (error) {
      console.error("Failed to upload attachment:", error?.message || error);
      toast.error("Failed to upload attachment");
    } finally {
      setUploadingAttachment(false);
      uploadEvent.target.value = "";
    }
  };

  const handleDownloadAttachment = async (attachmentId, fileName) => {
    try {
      const res = await axiosClient.get(
        `/api/Task/${taskId}/attachments/${attachmentId}/download`,
        {
          responseType: "blob",
        },
      );
      const url = globalThis.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName || "attachment");
      document.body.appendChild(link);
      link.click();
      link.remove();
      globalThis.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download attachment:", error?.message || error);
      toast.error("Failed to download attachment");
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (deletingAttachmentConfirmId !== attachmentId) {
      setDeletingAttachmentConfirmId(attachmentId);
      return;
    }
    setDeletingAttachmentConfirmId(null);
    setDeletingAttachmentId(attachmentId);
    try {
      await axiosClient.delete(
        `/api/Task/${taskId}/attachments/${attachmentId}`,
      );
      toast.success("Attachment deleted successfully!");
      setAttachments((prev) =>
        prev.filter((attachment) => attachment.attachmentId !== attachmentId),
      );
    } catch (error) {
      console.error("Failed to delete attachment:", error?.message || error);
      toast.error("Failed to delete attachment");
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  const getFileIcon = (fileName) => {
    const ext = fileName?.split(".").pop()?.toLowerCase();
    if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext))
      return { icon: "fa-file-image", color: "#8B5CF6" };
    if (["pdf"].includes(ext)) return { icon: "fa-file-pdf", color: "#EF4444" };
    if (["doc", "docx"].includes(ext))
      return { icon: "fa-file-word", color: "#2563EB" };
    if (["xls", "xlsx", "csv"].includes(ext))
      return { icon: "fa-file-excel", color: "#059669" };
    if (["zip", "rar", "7z"].includes(ext))
      return { icon: "fa-file-archive", color: "#F59E0B" };
    if (["mp4", "mov", "avi"].includes(ext))
      return { icon: "fa-file-video", color: "#EC4899" };
    return { icon: "fa-file-lines", color: "#6B7280" };
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleSubmitComment = async () => {
    if (!newComment.trim()) {
      return;
    }
    setSubmittingComment(true);
    try {
      await axiosClient.post("/api/Task/Comments", {
        id: "0",
        taskId: taskId, // capital I — match API field casing
        userId: String(currentUserId || "0"), // send actual current user
        comments: newComment.trim(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });

      toast.success("Comment added!");
      setNewComment("");
      fetchComments(taskId);
    } catch (error) {
      console.error("Failed to submit comment:", error?.message || error);
      toast.error("Failed to add comment");
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const buildDueDateString = (dateStr) => {
    if (!dateStr) {
      const defaultDate = new Date();
      defaultDate.setDate(defaultDate.getDate() + 7);
      return defaultDate.toISOString().slice(0, 19);
    }
    const now = new Date();
    return `${dateStr}T${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}:${String(now.getSeconds()).padStart(2, "0")}`;
  };

  const getStartDate = () => {
    if (formData.startDate) {
      return new Date(formData.startDate).toISOString();
    }
    if (originalTask?.startDate && !originalTask.startDate.startsWith("0001")) {
      return new Date(originalTask.startDate).toISOString();
    }
    return null;
  };

  const getEndDate = () => {
    if (formData.endDate) {
      return new Date(formData.endDate).toISOString();
    }
    if (originalTask?.endDate && !originalTask.endDate.startsWith("0001")) {
      return new Date(originalTask.endDate).toISOString();
    }
    return null;
  };

  const getModalTitle = () => {
    if (isEditMode) {
      return formData.subject || "Work Item";
    }
    return selectedType ? `New ${selectedType.label}` : "New Work Item";
  };

  const getOptionBackgroundColor = (selectState) => {
    if (selectState.isSelected) {
      return "#2563EB";
    }
    if (selectState.isFocused) {
      return "#EFF6FF";
    }
    return "white";
  };

  const handleSave = async () => {
    if (!formData.subject.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!formData.assignedTo) {
      toast.error("Please assign to someone");
      return;
    }
    if (!formData.priority) {
      toast.error("Please select a priority");
      return;
    }

    setLoading(true);
    try {
      if (isEditMode) {
        const payload = {
          Id: String(taskId),
          ParentId: formData.parentId || "0",
          TypeId:
            formData.taskType?.value ||
            originalTask?.taskTypeId ||
            originalTask?.typeId ||
            "",
          AssignedBy: String(
            formData.assignedBy?.value ||
              originalTask?.assignedById ||
              currentUserId ||
              "0",
          ),
          AssignedTo: String(formData.assignedTo.value),
          Subject: formData.subject.trim(),
          Description: formData.description || "",
          Priority: formData.priority.value,
          IsRead: Boolean(formData.isRead),
          Status: Number(formData.status),
          DueDate: buildDueDateString(formData.dueDate),
          StartDate: getStartDate(),
          EndDate: getEndDate(),
          Efforts: Number(formData.efforts) || 0,
          IsDeleted: Boolean(formData.isDeleted),
          CreatedBy: formData.createdBy || "0",
          UpdatedBy: String(currentUserId || ""),
          ModuleId: formData.moduleId?.value || originalTask?.moduleId || "",
        };
        await axiosClient.put(`/api/Task/${taskId}`, payload);
        toast.success("Work item updated successfully!");
        setIsFormDirty(false);
        isFirstLoad.current = true; // reset for if user stays on page
      } else {
        const payload = {
          id: "0",
          parentid: "0",
          typeId: formData.taskType?.value || "",
          assignedBy: String(currentUserId || "0"),
          assignedTo: formData.assignedTo.value,
          subject: formData.subject.trim(),
          description: formData.description || "",
          priority: formData.priority.value,
          isRead: false,
          status: 1,
          dueDate: buildDueDateString(formData.dueDate),
          startDate: formData.startDate
            ? new Date(formData.startDate).toISOString()
            : null,
          endDate: formData.endDate
            ? new Date(formData.endDate).toISOString()
            : null,
          efforts: Number(formData.efforts) || 0,
          isDeleted: false,
          moduleId: formData.moduleId?.value || "",
        };
        await axiosClient.post("/api/Task", payload);
        toast.success("Work item created successfully!");
        navigate("/task");
      }
    } catch (error) {
      console.error("Failed to save work item:", error?.message || error);
      const errorMessage = isEditMode
        ? "Failed to update work item"
        : "Failed to create work item";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const userOptions = usersList.map((user) => ({
    value: user.id,
    label: `${user.first_name} ${user.last_name}`,
  }));
  const taskTypeOptions = taskTypes.map((taskType) => ({
    value: taskType.task_type_id,
    label: taskType.name,
  }));

  const selectStyle = {
    control: (baseStyles, selectState) => ({
      ...baseStyles,
      minHeight: "38px",
      fontSize: "13px",
      borderRadius: "8px",
      borderColor: selectState.isFocused ? "#3B82F6" : "#E5E7EB",
      boxShadow: selectState.isFocused
        ? "0 0 0 3px rgba(59,130,246,0.12)"
        : "none",
      "&:hover": { borderColor: "#93C5FD" },
      transition: "all 0.15s ease",
    }),
    valueContainer: (baseStyles) => ({ ...baseStyles, padding: "0 10px" }),
    indicatorsContainer: (baseStyles) => ({ ...baseStyles, height: "38px" }),
    placeholder: (baseStyles) => ({
      ...baseStyles,
      color: "#9CA3AF",
      fontSize: "13px",
    }),
    singleValue: (baseStyles) => ({
      ...baseStyles,
      fontSize: "13px",
      color: "#111827",
      fontWeight: 500,
    }),
    option: (baseStyles, selectState) => ({
      ...baseStyles,
      fontSize: "13px",
      padding: "8px 12px",
      backgroundColor: getOptionBackgroundColor(selectState),
      color: selectState.isSelected ? "white" : "#111827",
      fontWeight: selectState.isSelected ? 600 : 400,
    }),
    menu: (baseStyles) => ({
      ...baseStyles,
      borderRadius: "10px",
      boxShadow: "0 10px 25px rgba(0,0,0,0.12)",
      border: "1px solid #E5E7EB",
    }),
  };

  const inputClass =
    "w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition-all duration-150 bg-white text-gray-800 placeholder-gray-400";

  if (isEditMode && fetchingTask) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-14 h-14 rounded-2xl bg-blue-600 flex items-center justify-center shadow-lg">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent" />
            </div>
          </div>
          <div className="text-center">
            <p className="text-sm font-semibold text-gray-700">
              Loading work item
            </p>
            <p className="text-xs text-gray-400 mt-0.5">Please wait...</p>
          </div>
        </div>
      </div>
    );
  }

  const typeConfig = selectedType
    ? WORK_ITEM_TYPES.find((workType) => workType.value === selectedType.value)
    : null;
  const statusInfo = STATUS_CONFIG[formData.status] || STATUS_CONFIG[1];

  // Helper to render loading/empty/list states to avoid nested ternaries
  const renderConditionalContent = (isLoading, isEmpty, emptyUI, contentUI) => {
    if (isLoading) {
      return (
        <div
          style={{ display: "flex", justifyContent: "center", padding: "24px" }}
        >
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent" />
        </div>
      );
    }
    return isEmpty ? emptyUI : contentUI;
  };

  return (
    <div className="min-h-screen" style={{ background: "#F8FAFC" }}>
      <style>{`
        .tab-btn { position: relative; }
        .tab-btn.active::after {
          content: '';
          position: absolute;
          bottom: -1px;
          left: 0; right: 0;
          height: 2px;
          background: #2563EB;
          border-radius: 2px 2px 0 0;
        }
        .field-label {
          display: block;
          font-size: 11px;
          font-weight: 700;
          color: #6B7280;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          margin-bottom: 5px;
        }
        .section-card {
          background: white;
          border-radius: 12px;
          border: 1px solid #E5E7EB;
          box-shadow: 0 1px 3px rgba(0,0,0,0.04);
        }
        .section-header {
          padding: 14px 18px;
          border-bottom: 1px solid #F3F4F6;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .section-header-title {
          font-size: 11px;
          font-weight: 800;
          color: #374151;
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }
        .type-badge {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 3px 10px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 700;
          border: 1px solid;
        }
        .comment-avatar {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 800;
          flex-shrink: 0;
        }
        .subtask-row:hover { background: #F0F7FF; border-color: #93C5FD; }
      `}</style>

      {/* ── TOP BAR ── */}
      <div
        style={{
          background: "white",
          borderBottom: "1px solid #E5E7EB",
          position: "sticky",
          top: 0,
          zIndex: 20,
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        }}
      >
        <div
          style={{
            padding: "0 24px",
            height: "56px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            maxWidth: "1400px",
            margin: "0 auto",
          }}
        >
          {/* Left */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              minWidth: 0,
            }}
          >
            <button
              onClick={() =>
                navigate("/task", { state: { activeView: fromView } })
              }
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "6px 10px",
                borderRadius: "8px",
                background: "transparent",
                border: "1px solid #E5E7EB",
                color: "#6B7280",
                fontSize: "13px",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#EFF6FF";
                e.currentTarget.style.color = "#2563EB";
                e.currentTarget.style.borderColor = "#93C5FD";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = "#6B7280";
                e.currentTarget.style.borderColor = "#E5E7EB";
              }}
            >
              <i className="fas fa-arrow-left" style={{ fontSize: "11px" }} />
              <span>Back</span>
            </button>

            <div
              style={{ width: "1px", height: "20px", background: "#E5E7EB" }}
            />

            {/* Type badge */}
            {typeConfig ? (
              <WorkItemIcon type={typeConfig.value} size={18} />
            ) : (
              <i
                className="fas fa-pen-to-square"
                style={{ color: "#9CA3AF", fontSize: "13px" }}
              />
            )}

            <div style={{ minWidth: 0 }}>
              <h1
                style={{
                  fontSize: "15px",
                  fontWeight: 800,
                  color: "#111827",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "400px",
                }}
              >
                {getModalTitle()}
              </h1>
            </div>

            {/* Status pill */}
            {isEditMode && (
              <span
                style={{
                  padding: "3px 10px",
                  background: statusInfo.bg,
                  color: statusInfo.color,
                  borderRadius: "20px",
                  fontSize: "11px",
                  fontWeight: 700,
                  border: `1px solid ${statusInfo.color}22`,
                }}
              >
                {statusInfo.label}
              </span>
            )}
          </div>

          {/* Right */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <button
              onClick={() =>
                navigate("/task", { state: { activeView: fromView } })
              }
              style={{
                padding: "7px 16px",
                borderRadius: "8px",
                background: "#F9FAFB",
                border: "1px solid #E5E7EB",
                color: "#6B7280",
                fontSize: "13px",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#F3F4F6";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#F9FAFB";
              }}
            >
              Discard
            </button>
            <button
              onClick={handleSave}
              disabled={loading || (isEditMode && !isFormDirty)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "7px",
                padding: "7px 20px",
                borderRadius: "8px",
                background:
                  loading || (isEditMode && !isFormDirty)
                    ? "#93C5FD"
                    : "#2563EB",
                cursor:
                  loading || (isEditMode && !isFormDirty)
                    ? "not-allowed"
                    : "pointer",
                border: "none",
                color: "white",
                fontSize: "13px",
                fontWeight: 700,
                transition: "all 0.15s",
                boxShadow: "0 1px 3px rgba(37,99,235,0.3)",
              }}
              onMouseEnter={(e) => {
                if (!loading && !(isEditMode && !isFormDirty))
                  e.currentTarget.style.background = "#1D4ED8";
              }}
              onMouseLeave={(e) => {
                if (!loading && !(isEditMode && !isFormDirty))
                  e.currentTarget.style.background = "#2563EB";
              }}
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              ) : (
                <i
                  className={`fas ${isEditMode ? "fa-save" : "fa-plus"}`}
                  style={{ fontSize: "11px" }}
                />
              )}
              {isEditMode ? "Save Changes" : "Create Work Item"}
            </button>
          </div>
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <div
        style={{
          maxWidth: "1400px",
          margin: "0 auto",
          padding: "24px 24px 60px",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 340px",
            gap: "20px",
            alignItems: "start",
          }}
        >
          {/* ── LEFT COLUMN ── */}
          <div
            style={{ display: "flex", flexDirection: "column", gap: "16px" }}
          >
            {/* Title Input */}
            <div
              className="section-card"
              style={{ padding: "0", overflow: "hidden" }}
            >
              <div
                style={{
                  padding: "9px 18px 9px 18px",
                  borderBottom: "1px solid #F3F4F6",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {typeConfig ? (
                  <span style={{ fontSize: "18px" }}>{typeConfig.icon}</span>
                ) : (
                  <i
                    className="fas fa-pen-to-square"
                    style={{ color: "#9CA3AF", fontSize: "13px" }}
                  />
                )}
                <span className="section-header-title">
                  Title<span style={{ color: "#EF4444" }}>*</span>
                </span>
              </div>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => handleChange("subject", e.target.value)}
                placeholder={
                  selectedType
                    ? `Enter ${selectedType.label} title...`
                    : "Enter work item title..."
                }
                style={{
                  width: "100%",
                  padding: "14px 18px",
                  fontSize: "16px",
                  fontWeight: 700,
                  color: "#111827",
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  boxSizing: "border-box",
                }}
              />
            </div>

            {/* Description */}
            <div className="section-card">
              <div className="section-header">
                <i
                  className="fas fa-align-left"
                  style={{ color: "#9CA3AF", fontSize: "12px" }}
                />
                <span className="section-header-title">Description</span>
              </div>
              <div style={{ padding: "14px 18px" }}>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  placeholder="Add description, acceptance criteria, steps to reproduce, or any relevant details..."
                  rows={7}
                  style={{
                    width: "100%",
                    fontSize: "13px",
                    color: "#374151",
                    resize: "vertical",
                    outline: "none",
                    border: "none",
                    background: "transparent",
                    lineHeight: "1.65",
                    boxSizing: "border-box",
                    fontFamily: "inherit",
                  }}
                />
              </div>
            </div>

            {/* Tabs (edit mode) */}
            {isEditMode && (
              <div className="section-card" style={{ overflow: "hidden" }}>
                <div
                  style={{
                    display: "flex",
                    borderBottom: "1px solid #F3F4F6",
                    padding: "0 6px",
                  }}
                >
                  {TABS.map((tabName) => (
                    <button
                      key={tabName}
                      onClick={() => setActiveTab(tabName)}
                      className={`tab-btn ${activeTab === tabName ? "active" : ""}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "12px 14px",
                        fontSize: "12px",
                        fontWeight: activeTab === tabName ? 700 : 600,
                        color: activeTab === tabName ? "#2563EB" : "#6B7280",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        transition: "color 0.15s",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {tabName === "Comments" && (
                        <i
                          className="fas fa-comment-dots"
                          style={{ fontSize: "11px" }}
                        />
                      )}
                      {tabName === "Sub Tasks" && (
                        <i
                          className="fas fa-list-check"
                          style={{ fontSize: "11px" }}
                        />
                      )}
                      {tabName === "Activity" && (
                        <i
                          className="fas fa-clock-rotate-left"
                          style={{ fontSize: "11px" }}
                        />
                      )}
                      {tabName === "Details" && (
                        <i
                          className="fas fa-sliders"
                          style={{ fontSize: "11px" }}
                        />
                      )}
                      {tabName === "Attachments" && (
                        <i
                          className="fas fa-paperclip"
                          style={{ fontSize: "11px" }}
                        />
                      )}
                      {tabName}
                      {tabName === "Attachments" && attachments.length > 0 && (
                        <span
                          style={{
                            padding: "1px 6px",
                            background: "#FFF7ED",
                            color: "#EA580C",
                            borderRadius: "10px",
                            fontSize: "10px",
                            fontWeight: 800,
                          }}
                        >
                          {attachments.length}
                        </span>
                      )}
                      {tabName === "Comments" && comments.length > 0 && (
                        <span
                          style={{
                            padding: "1px 6px",
                            background: "#EFF6FF",
                            color: "#2563EB",
                            borderRadius: "10px",
                            fontSize: "10px",
                            fontWeight: 800,
                          }}
                        >
                          {comments.length}
                        </span>
                      )}
                      {tabName === "Sub Tasks" && childTasks.length > 0 && (
                        <span
                          style={{
                            padding: "1px 6px",
                            background: "#ECFDF5",
                            color: "#059669",
                            borderRadius: "10px",
                            fontSize: "10px",
                            fontWeight: 800,
                          }}
                        >
                          {childTasks.length}
                        </span>
                      )}
                    </button>
                  ))}
                </div>

                {/* Tab: Details Configuration */}
                {activeTab === "Details" && (
                  <div
                    style={{
                      padding: "18px",
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "16px",
                    }}
                  >
                    {[
                      {
                        label: "Start Date",
                        icon: "fa-calendar-day",
                        field: "startDate",
                      },
                      {
                        label: "End Date",
                        icon: "fa-calendar-check",
                        field: "endDate",
                      },
                    ].map(
                      ({
                        label: fieldLabel,
                        icon: fieldIcon,
                        field: fieldName,
                      }) => (
                        <div key={fieldName}>
                          <label className="field-label">
                            <i
                              className={`fas ${fieldIcon}`}
                              style={{ marginRight: "5px", color: "#9CA3AF" }}
                            />
                            {fieldLabel}
                          </label>
                          <input
                            type="date"
                            value={formData[fieldName]}
                            onChange={(e) =>
                              handleChange(fieldName, e.target.value)
                            }
                            className={inputClass}
                          />
                        </div>
                      ),
                    )}
                    <div>
                      <label htmlFor="efforts" className="field-label">
                        <i
                          className="fas fa-hourglass-half"
                          style={{ marginRight: "5px", color: "#9CA3AF" }}
                        />
                        <span>Efforts (Story Points)</span>
                      </label>
                      <input
                        id="efforts"
                        type="number"
                        min="0"
                        value={formData.efforts}
                        onChange={(e) =>
                          handleChange("efforts", e.target.value)
                        }
                        placeholder="0"
                        className={inputClass}
                      />
                    </div>
                    {formData.parentId && formData.parentId !== "0" && (
                      <div>
                        <label htmlFor="parent-task" className="field-label">
                          <i
                            className="fas fa-sitemap"
                            style={{ marginRight: "5px", color: "#9CA3AF" }}
                          />
                          <span>Parent Task</span>
                        </label>
                        <div
                          id="parent-task"
                          style={{
                            padding: "8px 12px",
                            background: "#F9FAFB",
                            border: "1px solid #E5E7EB",
                            borderRadius: "8px",
                            fontSize: "13px",
                            color: "#374151",
                            fontFamily: "monospace",
                            fontWeight: 600,
                          }}
                        >
                          #{formData.parentId}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Tab: Comments Section */}
                {activeTab === "Comments" && (
                  <div style={{ padding: "18px" }}>
                    <div
                      style={{
                        display: "flex",
                        gap: "12px",
                        marginBottom: "20px",
                      }}
                    >
                      <div
                        className="comment-avatar"
                        style={{ background: "#2563EB", color: "white" }}
                      >
                        Me
                      </div>
                      <div style={{ flex: 1 }}>
                        <textarea
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          placeholder="Write a comment..."
                          rows={3}
                          style={{
                            width: "100%",
                            padding: "10px 14px",
                            fontSize: "13px",
                            border: "1px solid #E5E7EB",
                            borderRadius: "10px",
                            outline: "none",
                            resize: "none",
                            fontFamily: "inherit",
                            color: "#374151",
                            boxSizing: "border-box",
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = "#3B82F6";
                            e.target.style.boxShadow =
                              "0 0 0 3px rgba(59,130,246,0.12)";
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = "#E5E7EB";
                            e.target.style.boxShadow = "none";
                          }}
                        />
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            marginTop: "8px",
                          }}
                        >
                          <button
                            onClick={handleSubmitComment}
                            disabled={submittingComment || !newComment.trim()}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              padding: "7px 16px",
                              background:
                                submittingComment || !newComment.trim()
                                  ? "#93C5FD"
                                  : "#2563EB",
                              border: "none",
                              borderRadius: "8px",
                              color: "white",
                              fontSize: "12px",
                              fontWeight: 700,
                              cursor:
                                submittingComment || !newComment.trim()
                                  ? "not-allowed"
                                  : "pointer",
                            }}
                          >
                            {submittingComment ? (
                              <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent" />
                            ) : (
                              <i
                                className="fas fa-paper-plane"
                                style={{ fontSize: "10px" }}
                              />
                            )}
                            Post Comment
                          </button>
                        </div>
                      </div>
                    </div>
                    {renderConditionalContent(
                      loadingComments,
                      comments.length === 0,
                      <div
                        style={{
                          textAlign: "center",
                          padding: "32px",
                          color: "#9CA3AF",
                        }}
                      >
                        <i
                          className="fas fa-comments"
                          style={{
                            fontSize: "28px",
                            opacity: 0.3,
                            display: "block",
                            marginBottom: "8px",
                          }}
                        />
                        <p style={{ fontSize: "13px", fontWeight: 600 }}>
                          No comments yet
                        </p>
                        <p style={{ fontSize: "12px", marginTop: "2px" }}>
                          Be the first to add a comment
                        </p>
                      </div>,
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "10px",
                        }}
                      >
                        {comments.map((comment) => (
                          <div
                            key={
                              comment.id ||
                              `comment-${comment.userId}-${comment.createdAt}`
                            }
                            style={{
                              display: "flex",
                              gap: "10px",
                              padding: "12px 14px",
                              background: "#F9FAFB",
                              borderRadius: "10px",
                              border: "1px solid #F3F4F6",
                            }}
                          >
                            <div
                              className="comment-avatar"
                              style={{
                                background: "#EFF6FF",
                                color: "#2563EB",
                              }}
                            >
                              {comment.userName?.charAt(0)?.toUpperCase() ||
                                "U"}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  marginBottom: "4px",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: "12px",
                                    fontWeight: 700,
                                    color: "#111827",
                                  }}
                                >
                                  {comment.userName || "User"}
                                </span>
                                <span
                                  style={{ fontSize: "11px", color: "#9CA3AF" }}
                                >
                                  {comment.createdAt
                                    ? new Date(
                                        comment.createdAt,
                                      ).toLocaleString(undefined, {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })
                                    : ""}
                                </span>
                              </div>
                              <p
                                style={{
                                  fontSize: "13px",
                                  color: "#374151",
                                  lineHeight: "1.55",
                                  margin: 0,
                                }}
                              >
                                {comment.comments}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>,
                    )}
                  </div>
                )}

                {/* Tab: Sub Tasks List */}
                {activeTab === "Sub Tasks" && (
                  <div style={{ padding: "18px" }}>
                    {/* Header row: count + Add button */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: "14px",
                      }}
                    >
                      <p
                        style={{
                          fontSize: "11px",
                          fontWeight: 800,
                          color: "#9CA3AF",
                          textTransform: "uppercase",
                          letterSpacing: "0.07em",
                          margin: 0,
                        }}
                      >
                        {childTasks.length} Sub Task
                        {childTasks.length > 1 ? "s" : ""}
                      </p>
                      <button
                        onClick={() => setShowSubTaskForm((v) => !v)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                          padding: "6px 12px",
                          borderRadius: "8px",
                          background: showSubTaskForm ? "#F3F4F6" : "#2563EB",
                          border: "none",
                          color: showSubTaskForm ? "#374151" : "white",
                          fontSize: "12px",
                          fontWeight: 700,
                          cursor: "pointer",
                          transition: "all 0.15s",
                        }}
                      >
                        <i
                          className={`fas ${showSubTaskForm ? "fa-times" : "fa-plus"}`}
                          style={{ fontSize: "10px" }}
                        />
                        {showSubTaskForm ? "Cancel" : "Add Sub Task"}
                      </button>
                    </div>

                    {/* Inline Create Form */}
                    {showSubTaskForm && (
                      <div
                        style={{
                          marginBottom: "16px",
                          padding: "14px 16px",
                          background: "#F0F7FF",
                          borderRadius: "10px",
                          border: "1px solid #BFDBFE",
                          display: "flex",
                          flexDirection: "column",
                          gap: "10px",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "11px",
                            fontWeight: 800,
                            color: "#2563EB",
                            textTransform: "uppercase",
                            letterSpacing: "0.07em",
                            margin: 0,
                          }}
                        >
                          New Sub Task
                        </p>

                        {/* Title */}
                        <input
                          type="text"
                          value={subTaskFormData.subject}
                          onChange={(e) =>
                            setSubTaskFormData((p) => ({
                              ...p,
                              subject: e.target.value,
                            }))
                          }
                          placeholder="Sub task title *"
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            fontSize: "13px",
                            border: "1px solid #BFDBFE",
                            borderRadius: "8px",
                            outline: "none",
                            background: "white",
                            color: "#111827",
                            fontWeight: 500,
                            boxSizing: "border-box",
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = "#3B82F6";
                            e.target.style.boxShadow =
                              "0 0 0 3px rgba(59,130,246,0.12)";
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = "#BFDBFE";
                            e.target.style.boxShadow = "none";
                          }}
                        />

                        {/* Assignee + Priority row */}
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "10px",
                          }}
                        >
                          <div>
                            <label
                              htmlFor="subtask-assigned-to"
                              className="field-label"
                            >
                              Assign To *
                            </label>
                            <Select
                              inputId="subtask-assigned-to"
                              value={subTaskFormData.assignedTo}
                              onChange={(v) =>
                                setSubTaskFormData((p) => ({
                                  ...p,
                                  assignedTo: v,
                                }))
                              }
                              options={userOptions}
                              placeholder="Select user..."
                              isClearable
                              styles={selectStyle}
                            />
                          </div>
                          <div>
                            <label
                              htmlFor="subtask-priority"
                              className="field-label"
                            >
                              Priority *
                            </label>
                            <Select
                              inputId="subtask-priority"
                              value={subTaskFormData.priority}
                              onChange={(v) =>
                                setSubTaskFormData((p) => ({
                                  ...p,
                                  priority: v,
                                }))
                              }
                              options={priorityOptions}
                              placeholder="Select priority..."
                              isClearable
                              styles={selectStyle}
                            />
                          </div>
                        </div>

                        <div>
                          <label htmlFor="subtask-type" className="field-label">
                            Work Item Type
                          </label>
                          <Select
                            inputId="subtask-type"
                            value={subTaskFormData.taskType}
                            onChange={(v) =>
                              setSubTaskFormData((p) => ({ ...p, taskType: v }))
                            }
                            options={taskTypeOptions}
                            placeholder="Select type..."
                            isClearable
                            styles={selectStyle}
                          />
                        </div>

                        {/* Due Date */}
                        <div>
                          <label
                            htmlFor="subtask-duedate"
                            className="field-label"
                          >
                            Due Date
                          </label>
                          <input
                            id="subtask-duedate"
                            type="date"
                            value={subTaskFormData.dueDate}
                            onChange={(e) =>
                              setSubTaskFormData((p) => ({
                                ...p,
                                dueDate: e.target.value,
                              }))
                            }
                            className={inputClass}
                          />
                        </div>

                        {/* Description */}
                        <textarea
                          value={subTaskFormData.description}
                          onChange={(e) =>
                            setSubTaskFormData((p) => ({
                              ...p,
                              description: e.target.value,
                            }))
                          }
                          placeholder="Description (optional)"
                          rows={2}
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            fontSize: "13px",
                            border: "1px solid #BFDBFE",
                            borderRadius: "8px",
                            outline: "none",
                            resize: "none",
                            fontFamily: "inherit",
                            color: "#374151",
                            background: "white",
                            boxSizing: "border-box",
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = "#3B82F6";
                            e.target.style.boxShadow =
                              "0 0 0 3px rgba(59,130,246,0.12)";
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = "#BFDBFE";
                            e.target.style.boxShadow = "none";
                          }}
                        />

                        {/* Submit */}
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                          }}
                        >
                          <button
                            onClick={handleCreateSubTask}
                            disabled={creatingSubTask}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              padding: "8px 18px",
                              borderRadius: "8px",
                              background: creatingSubTask
                                ? "#93C5FD"
                                : "#2563EB",
                              border: "none",
                              color: "white",
                              fontSize: "12px",
                              fontWeight: 700,
                              cursor: creatingSubTask
                                ? "not-allowed"
                                : "pointer",
                              boxShadow: "0 1px 3px rgba(37,99,235,0.25)",
                            }}
                          >
                            {creatingSubTask ? (
                              <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent" />
                            ) : (
                              <i
                                className="fas fa-plus"
                                style={{ fontSize: "10px" }}
                              />
                            )}
                            {creatingSubTask
                              ? "Creating..."
                              : "Create Sub Task"}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Existing sub tasks list */}
                    {renderConditionalContent(
                      loadingChildTasks,
                      childTasks.length === 0,
                      <div
                        style={{
                          textAlign: "center",
                          padding: "32px",
                          color: "#9CA3AF",
                        }}
                      >
                        <i
                          className="fas fa-list-check"
                          style={{
                            fontSize: "28px",
                            opacity: 0.3,
                            display: "block",
                            marginBottom: "8px",
                          }}
                        />
                        <p style={{ fontSize: "13px", fontWeight: 600 }}>
                          No sub tasks yet
                        </p>
                        <p style={{ fontSize: "12px", marginTop: "2px" }}>
                          Click "Add Sub Task" above to create one
                        </p>
                      </div>,
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "6px",
                        }}
                      >
                        {childTasks.map((childTask) => (
                          <button
                            key={childTask.id}
                            onClick={() =>
                              navigate(`/task/edit/${childTask.id}`)
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                navigate(`/task/edit/${childTask.id}`);
                              }
                            }}
                            type="button"
                            className="subtask-row"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                              padding: "10px 14px",
                              background: "#F9FAFB",
                              border: "1px solid #E5E7EB",
                              borderRadius: "8px",
                              cursor: "pointer",
                              transition: "all 0.15s",
                              width: "100%",
                              textAlign: "left",
                              fontFamily: "inherit",
                              fontSize: "inherit",
                            }}
                          >
                            {/* <span
                              style={{
                                color: "#2563EB",
                                fontFamily: "monospace",
                                fontSize: "11px",
                                fontWeight: 700,
                                background: "#EFF6FF",
                                padding: "2px 7px",
                                borderRadius: "5px",
                              }}
                            >
                              #{childTask.id?.slice(0, 6)}
                            </span> */}
                            <span
                              style={{
                                flex: 1,
                                fontSize: "13px",
                                fontWeight: 600,
                                color: "#374151",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {childTask.subject}
                            </span>
                            <i
                              className="fas fa-chevron-right"
                              style={{ color: "#9CA3AF", fontSize: "10px" }}
                            />
                          </button>
                        ))}
                      </div>,
                    )}
                  </div>
                )}

                {/* Tab: Attachments Management */}
                {activeTab === "Attachments" && (
                  <div style={{ padding: "18px" }}>
                    {/* Upload Area */}
                    <button
                      type="button"
                      aria-label="Upload area. Drag files here or click to upload"
                      style={{
                        border: "2px dashed #E5E7EB",
                        borderRadius: "12px",
                        padding: "24px",
                        textAlign: "center",
                        marginBottom: "16px",
                        background: "#FAFAFA",
                        transition: "all 0.2s",
                        cursor: "pointer",
                        width: "100%",
                        fontFamily: "inherit",
                        fontSize: "inherit",
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.currentTarget.style.borderColor = "#3B82F6";
                        e.currentTarget.style.background = "#EFF6FF";
                      }}
                      onDragLeave={(e) => {
                        e.currentTarget.style.borderColor = "#E5E7EB";
                        e.currentTarget.style.background = "#FAFAFA";
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.currentTarget.style.borderColor = "#E5E7EB";
                        e.currentTarget.style.background = "#FAFAFA";
                        const dt = {
                          target: { files: e.dataTransfer.files, value: "" },
                        };
                        handleUploadAttachment(dt);
                      }}
                      onClick={() =>
                        document
                          .getElementById("attachment-upload-input")
                          .click()
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          document
                            .getElementById("attachment-upload-input")
                            .click();
                        }
                      }}
                    >
                      <input
                        id="attachment-upload-input"
                        type="file"
                        multiple
                        style={{ display: "none" }}
                        onChange={handleUploadAttachment}
                      />
                      {uploadingAttachment ? (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent" />
                          <p
                            style={{
                              fontSize: "13px",
                              fontWeight: 600,
                              color: "#2563EB",
                            }}
                          >
                            Uploading...
                          </p>
                        </div>
                      ) : (
                        <>
                          <div
                            style={{
                              width: "44px",
                              height: "44px",
                              borderRadius: "12px",
                              background: "#EFF6FF",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              margin: "0 auto 10px",
                            }}
                          >
                            <i
                              className="fas fa-cloud-arrow-up"
                              style={{ fontSize: "20px", color: "#2563EB" }}
                            />
                          </div>
                          <p
                            style={{
                              fontSize: "13px",
                              fontWeight: 700,
                              color: "#374151",
                              margin: "0 0 3px",
                            }}
                          >
                            Drop files here or click to upload
                          </p>
                          <p
                            style={{
                              fontSize: "11px",
                              color: "#9CA3AF",
                              margin: 0,
                            }}
                          >
                            Supports images, PDFs, documents, and more
                          </p>
                        </>
                      )}
                    </button>

                    {/* Attachment List */}
                    {renderConditionalContent(
                      loadingAttachments,
                      attachments.length === 0,
                      <div
                        style={{
                          textAlign: "center",
                          padding: "24px",
                          color: "#9CA3AF",
                        }}
                      >
                        <i
                          className="fas fa-paperclip"
                          style={{
                            fontSize: "26px",
                            opacity: 0.25,
                            display: "block",
                            marginBottom: "8px",
                          }}
                        />
                        <p
                          style={{
                            fontSize: "13px",
                            fontWeight: 600,
                            margin: 0,
                          }}
                        >
                          No attachments yet
                        </p>
                        <p style={{ fontSize: "12px", marginTop: "3px" }}>
                          Upload files using the area above
                        </p>
                      </div>,
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "6px",
                        }}
                      >
                        {attachments.map((att) => {
                          const { icon, color } = getFileIcon(
                            att.fileName || att.name,
                          );
                          const isDeleting =
                            deletingAttachmentId === att.attachmentId;
                          const deleteButtonContent =
                            deletingAttachmentConfirmId === att.attachmentId ? (
                              <>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="11"
                                  height="11"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                  <line x1="12" y1="9" x2="12" y2="13" />
                                  <line x1="12" y1="17" x2="12.01" y2="17" />
                                </svg>
                                <span>Confirm?</span>
                              </>
                            ) : (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="13"
                                height="13"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                <path d="M10 11v6" />
                                <path d="M14 11v6" />
                                <path d="M9 6V4h6v2" />
                              </svg>
                            );

                          return (
                            <section
                              key={att.attachmentId}
                              aria-label={att.fileName || "Attachment"}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                padding: "10px 14px",
                                background: "#F9FAFB",
                                border: "1px solid #E5E7EB",
                                borderRadius: "10px",
                                transition: "all 0.15s",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = "#F0F7FF";
                                e.currentTarget.style.borderColor = "#93C5FD";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = "#F9FAFB";
                                e.currentTarget.style.borderColor = "#E5E7EB";
                              }}
                              onFocus={(e) => {
                                e.currentTarget.style.background = "#F0F7FF";
                                e.currentTarget.style.borderColor = "#93C5FD";
                              }}
                              onBlur={(e) => {
                                e.currentTarget.style.background = "#F9FAFB";
                                e.currentTarget.style.borderColor = "#E5E7EB";
                              }}
                            >
                              {/* File Icon */}
                              <div
                                style={{
                                  width: "36px",
                                  height: "36px",
                                  borderRadius: "9px",
                                  background: `${color}15`,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                <i
                                  className={`fas ${icon}`}
                                  style={{ fontSize: "16px", color }}
                                />
                              </div>

                              {/* File Information */}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <p
                                  style={{
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    color: "#111827",
                                    margin: 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {att.fileName || "Unnamed file"}
                                </p>

                                {/* Row 2: size + date */}
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    marginTop: "3px",
                                    flexWrap: "wrap",
                                  }}
                                >
                                  {att.fileSizeBytes ? (
                                    <span
                                      style={{
                                        fontSize: "11px",
                                        color: "#9CA3AF",
                                      }}
                                    >
                                      {formatFileSize(att.fileSizeBytes)}
                                    </span>
                                  ) : null}
                                  {att.uploadedAt && (
                                    <span
                                      style={{
                                        fontSize: "11px",
                                        color: "#9CA3AF",
                                      }}
                                    >
                                      {new Date(
                                        att.uploadedAt,
                                      ).toLocaleDateString(undefined, {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                      })}
                                    </span>
                                  )}
                                  {att.fileExtension && (
                                    <span
                                      style={{
                                        fontSize: "10px",
                                        fontWeight: 700,
                                        padding: "1px 6px",
                                        background: "#F3F4F6",
                                        color: "#6B7280",
                                        borderRadius: "4px",
                                        textTransform: "uppercase",
                                      }}
                                    >
                                      {att.fileExtension}
                                    </span>
                                  )}
                                </div>

                                {/* Row 3: uploaded by */}
                                {att.uploadedBy && (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px",
                                      marginTop: "3px",
                                    }}
                                  >
                                    <i
                                      className="fas fa-user-circle"
                                      style={{
                                        fontSize: "10px",
                                        color: "#9CA3AF",
                                      }}
                                    />
                                    <span
                                      style={{
                                        fontSize: "11px",
                                        color: "#6B7280",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {resolveUserName(att.uploadedBy)}
                                    </span>
                                  </div>
                                )}
                              </div>

                              {/* Actions */}
                              <div
                                style={{
                                  display: "flex",
                                  gap: "6px",
                                  flexShrink: 0,
                                }}
                              >
                                <button
                                  onClick={() =>
                                    handleDownloadAttachment(
                                      att.attachmentId,
                                      att.fileName,
                                    )
                                  }
                                  title="Download"
                                  style={{
                                    width: "30px",
                                    height: "30px",
                                    borderRadius: "7px",
                                    background: "#EFF6FF",
                                    border: "1px solid #BFDBFE",
                                    color: "#2563EB",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    transition: "all 0.15s",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background =
                                      "#DBEAFE";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background =
                                      "#EFF6FF";
                                  }}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="13"
                                    height="13"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="7 10 12 15 17 10" />
                                    <line x1="12" y1="15" x2="12" y2="3" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() =>
                                    handleDeleteAttachment(att.attachmentId)
                                  }
                                  disabled={isDeleting}
                                  title={
                                    deletingAttachmentConfirmId ===
                                    att.attachmentId
                                      ? "Click again to confirm"
                                      : "Delete"
                                  }
                                  style={{
                                    width:
                                      deletingAttachmentConfirmId ===
                                      att.attachmentId
                                        ? "auto"
                                        : "30px",
                                    padding:
                                      deletingAttachmentConfirmId ===
                                      att.attachmentId
                                        ? "0 8px"
                                        : "0",
                                    height: "30px",
                                    borderRadius: "7px",
                                    background:
                                      deletingAttachmentConfirmId ===
                                      att.attachmentId
                                        ? "#FEE2E2"
                                        : "#FEF2F2",
                                    border: "1px solid #FECACA",
                                    color: isDeleting ? "#FCA5A5" : "#EF4444",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "4px",
                                    cursor: isDeleting
                                      ? "not-allowed"
                                      : "pointer",
                                    fontSize: "11px",
                                    fontWeight: 700,
                                    transition: "all 0.15s",
                                    whiteSpace: "nowrap",
                                  }}
                                  onMouseLeave={() => {
                                    if (
                                      deletingAttachmentConfirmId ===
                                      att.attachmentId
                                    ) {
                                      setDeletingAttachmentConfirmId(null);
                                    }
                                  }}
                                >
                                  {isDeleting ? (
                                    <div className="animate-spin rounded-full h-3 w-3 border-2 border-red-400 border-t-transparent" />
                                  ) : (
                                    deleteButtonContent
                                  )}
                                </button>
                              </div>
                            </section>
                          );
                        })}
                      </div>,
                    )}
                  </div>
                )}

                {/* Tab: Activity History */}
                {activeTab === "Activity" && (
                  <div style={{ padding: "18px" }}>
                    {(() => {
                      if (loadingActivity) {
                        return (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              padding: "32px",
                            }}
                          >
                            <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent" />
                          </div>
                        );
                      }
                      return activitySummary ? (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "16px",
                          }}
                        >
                          {/* Summary Header */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "12px 16px",
                              background: "#F8FAFC",
                              borderRadius: "10px",
                              border: "1px solid #E5E7EB",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <div
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  borderRadius: "8px",
                                  background: "#EFF6FF",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <i
                                  className="fas fa-chart-bar"
                                  style={{ fontSize: "13px", color: "#2563EB" }}
                                />
                              </div>
                              <div>
                                <p
                                  style={{
                                    fontSize: "12px",
                                    fontWeight: 800,
                                    color: "#111827",
                                    margin: 0,
                                  }}
                                >
                                  Total Activities
                                </p>
                                <p
                                  style={{
                                    fontSize: "11px",
                                    color: "#6B7280",
                                    margin: 0,
                                  }}
                                >
                                  {activitySummary.lastActivity
                                    ? `Last updated ${new Date(activitySummary.lastActivity?.createdAt).toLocaleString()}`
                                    : "No activity recorded yet"}
                                </p>
                              </div>
                            </div>
                            <span
                              style={{
                                fontSize: "22px",
                                fontWeight: 900,
                                color: "#2563EB",
                                background: "#EFF6FF",
                                padding: "4px 14px",
                                borderRadius: "10px",
                                border: "1px solid #BFDBFE",
                              }}
                            >
                              {activitySummary.totalActivities}
                            </span>
                          </div>

                          {/* Recent Activities */}
                          {activitySummary.recentActivities?.length > 0 && (
                            <div>
                              <p
                                style={{
                                  fontSize: "10px",
                                  fontWeight: 800,
                                  color: "#9CA3AF",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.08em",
                                  margin: "0 0 10px",
                                }}
                              >
                                Recent Activities
                              </p>
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "6px",
                                }}
                              >
                                {activitySummary.recentActivities.map(
                                  (activity) => {
                                    const cfg = ACTIVITY_TYPE_CONFIG[
                                      activity.activityTypeDisplay
                                    ] || {
                                      color: "#6B7280",
                                      bg: "#F3F4F6",
                                      icon: "fa-circle-dot",
                                    };
                                    const performedByName = resolveUserName(
                                      activity.performedBy,
                                    );

                                    return (
                                      <div
                                        key={activity.activityId}
                                        style={{
                                          display: "flex",
                                          alignItems: "flex-start",
                                          gap: "12px",
                                          padding: "12px 14px",
                                          background: "#F9FAFB",
                                          border: "1px solid #F3F4F6",
                                          borderRadius: "10px",
                                        }}
                                      >
                                        {/* Icon */}
                                        <div
                                          style={{
                                            width: "32px",
                                            height: "32px",
                                            borderRadius: "8px",
                                            background: cfg.bg,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            flexShrink: 0,
                                            border: `1px solid ${cfg.color}20`,
                                          }}
                                        >
                                          <cfg.SvgIcon color={cfg.color} />
                                        </div>

                                        <div style={{ flex: 1, minWidth: 0 }}>
                                          {/* Top row: description + timestamp */}
                                          <div
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "space-between",
                                              gap: "8px",
                                              flexWrap: "wrap",
                                            }}
                                          >
                                            <p
                                              style={{
                                                fontSize: "12px",
                                                fontWeight: 700,
                                                color: "#111827",
                                                margin: 0,
                                              }}
                                            >
                                              {activity.activityDescription ||
                                                activity.activityTypeDisplay}
                                            </p>
                                            {activity.createdAt && (
                                              <span
                                                style={{
                                                  fontSize: "11px",
                                                  color: "#9CA3AF",
                                                  whiteSpace: "nowrap",
                                                }}
                                              >
                                                {new Date(
                                                  activity.createdAt,
                                                ).toLocaleString(undefined, {
                                                  day: "2-digit",
                                                  month: "short",
                                                  year: "numeric",
                                                  hour: "2-digit",
                                                  minute: "2-digit",
                                                })}
                                              </span>
                                            )}
                                          </div>

                                          {/* Performed by */}
                                          <div
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "4px",
                                              marginTop: "3px",
                                            }}
                                          >
                                            <i
                                              className="fas fa-user-circle"
                                              style={{
                                                fontSize: "10px",
                                                color: "#9CA3AF",
                                              }}
                                            />
                                            <span
                                              style={{
                                                fontSize: "11px",
                                                color: "#6B7280",
                                                fontWeight: 600,
                                              }}
                                            >
                                              {performedByName}
                                            </span>
                                            <span
                                              style={{
                                                marginLeft: "6px",
                                                padding: "1px 7px",
                                                background: cfg.bg,
                                                color: cfg.color,
                                                borderRadius: "10px",
                                                fontSize: "10px",
                                                fontWeight: 700,
                                                border: `1px solid ${cfg.color}30`,
                                              }}
                                            >
                                              {activity.activityTypeDisplay}
                                            </span>
                                          </div>

                                          {/* Before / After values */}
                                          {(() => {
                                            if (
                                              !activity.oldValue &&
                                              activity.newValue
                                            ) {
                                              const resolved =
                                                resolveActivityValue(
                                                  activity.newValue,
                                                );
                                              const display =
                                                formatActivityValue(resolved);
                                              return display ? (
                                                <div
                                                  style={{
                                                    marginTop: "8px",
                                                    padding: "8px 10px",
                                                    background: "white",
                                                    borderRadius: "7px",
                                                    border: "1px solid #E5E7EB",
                                                    fontSize: "11px",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    gap: "4px",
                                                  }}
                                                >
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      gap: "6px",
                                                      alignItems: "flex-start",
                                                    }}
                                                  >
                                                    <span
                                                      style={{
                                                        padding: "1px 6px",
                                                        background: "#ECFDF5",
                                                        color: "#059669",
                                                        borderRadius: "4px",
                                                        fontSize: "10px",
                                                        fontWeight: 800,
                                                        flexShrink: 0,
                                                        marginTop: "1px",
                                                      }}
                                                    >
                                                      After
                                                    </span>
                                                    <span
                                                      style={{
                                                        color: "#374151",
                                                        lineHeight: "1.5",
                                                        fontWeight: 500,
                                                      }}
                                                    >
                                                      {display}
                                                    </span>
                                                  </div>
                                                </div>
                                              ) : null;
                                            }

                                            // For updates — compute and show only changed fields
                                            if (
                                              activity.oldValue &&
                                              activity.newValue
                                            ) {
                                              const { oldDisplay, newDisplay } =
                                                getChangedFields(
                                                  activity.oldValue,
                                                  activity.newValue,
                                                );
                                              if (!oldDisplay && !newDisplay)
                                                return null;
                                              return (
                                                <div
                                                  style={{
                                                    marginTop: "8px",
                                                    padding: "8px 10px",
                                                    background: "white",
                                                    borderRadius: "7px",
                                                    border: "1px solid #E5E7EB",
                                                    fontSize: "11px",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    gap: "4px",
                                                  }}
                                                >
                                                  {oldDisplay && (
                                                    <div
                                                      style={{
                                                        display: "flex",
                                                        gap: "6px",
                                                        alignItems:
                                                          "flex-start",
                                                      }}
                                                    >
                                                      <span
                                                        style={{
                                                          padding: "1px 6px",
                                                          background: "#FEF2F2",
                                                          color: "#EF4444",
                                                          borderRadius: "4px",
                                                          fontSize: "10px",
                                                          fontWeight: 800,
                                                          flexShrink: 0,
                                                          marginTop: "1px",
                                                        }}
                                                      >
                                                        Before
                                                      </span>
                                                      <span
                                                        style={{
                                                          color: "#6B7280",
                                                          lineHeight: "1.5",
                                                        }}
                                                      >
                                                        {oldDisplay}
                                                      </span>
                                                    </div>
                                                  )}
                                                  {newDisplay && (
                                                    <div
                                                      style={{
                                                        display: "flex",
                                                        gap: "6px",
                                                        alignItems:
                                                          "flex-start",
                                                      }}
                                                    >
                                                      <span
                                                        style={{
                                                          padding: "1px 6px",
                                                          background: "#ECFDF5",
                                                          color: "#059669",
                                                          borderRadius: "4px",
                                                          fontSize: "10px",
                                                          fontWeight: 800,
                                                          flexShrink: 0,
                                                          marginTop: "1px",
                                                        }}
                                                      >
                                                        After
                                                      </span>
                                                      <span
                                                        style={{
                                                          color: "#374151",
                                                          lineHeight: "1.5",
                                                          fontWeight: 500,
                                                        }}
                                                      >
                                                        {newDisplay}
                                                      </span>
                                                    </div>
                                                  )}
                                                </div>
                                              );
                                            }

                                            return null;
                                          })()}
                                        </div>
                                      </div>
                                    );
                                  },
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div
                          style={{
                            textAlign: "center",
                            padding: "40px",
                            color: "#9CA3AF",
                          }}
                        >
                          <i
                            className="fas fa-clock-rotate-left"
                            style={{
                              fontSize: "28px",
                              opacity: 0.3,
                              display: "block",
                              marginBottom: "8px",
                            }}
                          />
                          <p style={{ fontSize: "13px", fontWeight: 600 }}>
                            No activity data available
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            {/* Discussion (create mode only) */}
            {!isEditMode && (
              <div className="section-card">
                <div className="section-header">
                  <i
                    className="fas fa-comment-dots"
                    style={{ color: "#9CA3AF", fontSize: "12px" }}
                  />
                  <span className="section-header-title">Discussion</span>
                </div>
                <div style={{ padding: "14px 18px" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "10px",
                    }}
                  >
                    <div
                      className="comment-avatar"
                      style={{ background: "#2563EB", color: "white" }}
                    >
                      Me
                    </div>
                    <textarea
                      placeholder="Add a note or initial comment..."
                      rows={3}
                      style={{
                        flex: 1,
                        padding: "10px 14px",
                        fontSize: "13px",
                        border: "1px solid #E5E7EB",
                        borderRadius: "10px",
                        outline: "none",
                        resize: "none",
                        fontFamily: "inherit",
                        color: "#374151",
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#3B82F6";
                        e.target.style.boxShadow =
                          "0 0 0 3px rgba(59,130,246,0.12)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#E5E7EB";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── RIGHT COLUMN ── */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "14px",
              position: "sticky",
              top: "72px",
            }}
          >
            {/* Details Card */}
            <div className="section-card">
              <div className="section-header">
                <i
                  className="fas fa-sliders"
                  style={{ color: "#9CA3AF", fontSize: "12px" }}
                />
                <span className="section-header-title">Details</span>
              </div>
              <div
                style={{
                  padding: "16px 16px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "14px",
                }}
              >
                {/* Assigned To */}
                <div>
                  <label htmlFor="assigned-to" className="field-label">
                    Assigned To <span style={{ color: "#EF4444" }}>*</span>
                  </label>
                  <Select
                    inputId="assigned-to"
                    value={formData.assignedTo}
                    onChange={(v) => handleChange("assignedTo", v)}
                    options={userOptions}
                    placeholder="Select assignee..."
                    isClearable
                    styles={selectStyle}
                  />
                </div>

                {/* Priority */}
                <div>
                  <label htmlFor="priority" className="field-label">
                    Priority <span style={{ color: "#EF4444" }}>*</span>
                  </label>
                  <Select
                    inputId="priority"
                    value={formData.priority}
                    onChange={(v) => handleChange("priority", v)}
                    options={priorityOptions}
                    placeholder="Select priority..."
                    isClearable
                    styles={selectStyle}
                  />
                </div>

                {/* Divider */}
                <div
                  style={{
                    height: "1px",
                    background: "#F3F4F6",
                    margin: "0 -16px",
                  }}
                />

                {/* Status */}
                <div>
                  <label htmlFor="status" className="field-label">
                    Status
                  </label>
                  <select
                    id="status"
                    value={formData.status}
                    onChange={(e) =>
                      handleChange("status", Number(e.target.value))
                    }
                    style={{
                      width: "100%",
                      padding: "8px 10px",
                      fontSize: "13px",
                      border: "1px solid #E5E7EB",
                      borderRadius: "8px",
                      outline: "none",
                      background: "white",
                      color: "#111827",
                      fontWeight: 500,
                      cursor: "pointer",
                      appearance: "auto",
                    }}
                  >
                    <option value={1}>New</option>
                    <option value={2}>In Progress</option>
                    <option value={3}>Closed</option>
                    <option value={4}>Blocked</option>
                    <option value="5">On Hold</option>
                    <option value="6">Resolved</option>
                  </select>
                </div>

                {/* Task Type */}
                {/* Task Type — only show if no work item type was pre-selected */}
                {(isEditMode || !selectedType) && (
                  <div>
                    <label htmlFor="task-type" className="field-label">
                      Work Item
                    </label>
                    <Select
                      inputId="task-type"
                      value={formData.taskType}
                      onChange={(v) => handleChange("taskType", v)}
                      options={taskTypeOptions}
                      placeholder="Select type..."
                      isClearable
                      styles={selectStyle}
                    />
                  </div>
                )}

                {/* Module */}
                {/* Module */}
                <div>
                  <label htmlFor="module-select" className="field-label">
                    <i
                      className="fas fa-cubes"
                      style={{ marginRight: "4px", color: "#9CA3AF" }}
                    />
                    <span>Module</span>
                  </label>
                  <Select
                    inputId="module-select"
                    value={formData.moduleId}
                    onInputChange={(val) => setModuleSearchInput(val)}
                    inputValue={moduleSearchInput}
                    options={[
                      ...modules.map((m) => ({
                        value: m.moduleId,
                        label: m.moduleName,
                      })),
                      ...(moduleSearchInput.trim() &&
                      !modules.some(
                        (m) =>
                          m.moduleName.toLowerCase() ===
                          moduleSearchInput.trim().toLowerCase(),
                      )
                        ? [
                            {
                              value: "__create__",
                              label: `+ Create "${moduleSearchInput.trim()}"`,
                              isCreate: true,
                            },
                          ]
                        : []),
                    ]}
                    onChange={async (selected) => {
                      if (!selected) {
                        handleChange("moduleId", null);
                        return;
                      }
                      if (selected.value === "__create__") {
                        const name = moduleSearchInput.trim();
                        if (!name) return;
                        setCreatingModule(true);
                        try {
                          const res = await axiosClient.post(
                            "/api/Task/modules",
                            JSON.stringify(name),
                            { headers: { "Content-Type": "application/json" } },
                          );
                          const created = res?.data;
                          if (created) {
                            setModules((prev) => [...prev, created]);
                            handleChange("moduleId", {
                              value: created.moduleId,
                              label: created.moduleName,
                            });
                            toast.success(
                              `Module "${created.moduleName}" created!`,
                            );
                          }
                        } catch (error) {
                          console.error(
                            "Failed to create module:",
                            error?.message || error,
                          );
                          toast.error("Failed to create module");
                        } finally {
                          setCreatingModule(false);
                          setModuleSearchInput("");
                        }
                        return;
                      }
                      handleChange("moduleId", selected);
                      setModuleSearchInput("");
                    }}
                    placeholder={
                      loadingModules
                        ? "Loading modules..."
                        : "Search or create module..."
                    }
                    isClearable
                    isLoading={loadingModules || creatingModule}
                    styles={{
                      ...selectStyle,
                      option: (base, state) => {
                        const isCreateOption = state.data?.isCreate;
                        const isSelected = state.isSelected;
                        const isFocused = state.isFocused;

                        // Determine background color
                        let bgColor = "white";
                        if (isSelected) {
                          bgColor = "#2563EB";
                        } else if (isFocused && isCreateOption) {
                          bgColor = "#F0F7FF";
                        } else if (isFocused) {
                          bgColor = "#EFF6FF";
                        }

                        // Determine text color
                        let textColor = "#111827";
                        if (isSelected) {
                          textColor = "white";
                        } else if (isCreateOption) {
                          textColor = "#2563EB";
                        }

                        // Determine text weight
                        let textWeight = 400;
                        if (isCreateOption) {
                          textWeight = 700;
                        } else if (isSelected) {
                          textWeight = 600;
                        }

                        // Determine top border
                        let topBorder = "none";
                        if (isCreateOption) {
                          topBorder = "1px solid #E5E7EB";
                        }

                        return {
                          ...base,
                          fontSize: "13px",
                          padding: "8px 12px",
                          backgroundColor: bgColor,
                          color: textColor,
                          fontWeight: textWeight,
                          borderTop: topBorder,
                        };
                      },
                    }}
                  />
                </div>

                {/* Divider */}
                <div
                  style={{
                    height: "1px",
                    background: "#F3F4F6",
                    margin: "0 -16px",
                  }}
                />

                {/* Dates */}
                <div>
                  <label htmlFor="due-date" className="field-label">
                    <i
                      className="fas fa-calendar"
                      style={{ marginRight: "4px", color: "#9CA3AF" }}
                    />
                    <span>Due Date</span>
                  </label>
                  <input
                    id="due-date"
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) => handleChange("dueDate", e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>
            </div>

            {/* Quick Info Card (edit mode) */}
            {isEditMode && formData.parentId && formData.parentId !== "0" && (
              <div className="section-card" style={{ padding: "14px 16px" }}>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <div
                    style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "7px",
                      background: "#F3F4F6",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <i
                      className="fas fa-sitemap"
                      style={{ fontSize: "11px", color: "#6B7280" }}
                    />
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: "10px",
                        fontWeight: 700,
                        color: "#9CA3AF",
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                      }}
                    >
                      Parent Task
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: 700,
                        color: "#374151",
                        fontFamily: "monospace",
                      }}
                    >
                      {formData.parentId}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateWorkItem;
